import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Snackbar,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import { useState } from "react";

const steps = [
  "Personal Details",
  "Education",
  "Experience",
  "Skills",
  "Review",
];

const ApplyModal = ({ open, onClose, job, showSnackbar }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    linkedin: "",
    skills: [""],
    education: [
      { degree: "", university: "", year: "", grade: "", certifications: [""] },
    ],
    experience: [
      {
        company: "",
        jobTitle: "",
        startDate: "",
        endDate: "",
        responsibilities: "",
      },
    ],
    resume: null,
    agree: false,
  });
  const [errors, setErrors] = useState({});

  const handleChange = (e, section = null, index = null, subIndex = null) => {
    const { name, value, type, checked, files } = e.target;
    const newValue =
      type === "checkbox" ? checked : type === "file" ? files[0] : value;

    if (section && index !== null) {
      const updated = [...formData[section]];

      if (subIndex !== null) {
        const subArray = [...updated[index][name]];
        subArray[subIndex] = newValue;
        updated[index][name] = subArray;
      } else {
        typeof updated[index] === "string"
          ? (updated[index] = newValue)
          : (updated[index][name] = newValue);
      }

      setFormData({ ...formData, [section]: updated });
      return;
    }

    setFormData({ ...formData, [name]: newValue });
  };

  // Education handlers
  const addEducation = () =>
    setFormData({
      ...formData,
      education: [
        ...formData.education,
        {
          degree: "",
          university: "",
          year: "",
          grade: "",
          certifications: [""],
        },
      ],
    });

  const removeEducation = (idx) =>
    setFormData({
      ...formData,
      education: formData.education.filter((_, i) => i !== idx),
    });

  const addCertification = (eduIdx) => {
    const updated = [...formData.education];
    updated[eduIdx].certifications.push("");
    setFormData({ ...formData, education: updated });
  };

  const removeCertification = (eduIdx, certIdx) => {
    const updated = [...formData.education];
    updated[eduIdx].certifications.splice(certIdx, 1);
    setFormData({ ...formData, education: updated });
  };

  // Experience handlers
  const addExperience = () =>
    setFormData({
      ...formData,
      experience: [
        ...formData.experience,
        {
          company: "",
          jobTitle: "",
          startDate: "",
          endDate: "",
          responsibilities: "",
        },
      ],
    });

  const removeExperience = (idx) =>
    setFormData({
      ...formData,
      experience: formData.experience.filter((_, i) => i !== idx),
    });

  // Skills handlers
  const addSkill = () =>
    setFormData({ ...formData, skills: [...formData.skills, ""] });

  const removeSkill = (idx) =>
    setFormData({
      ...formData,
      skills: formData.skills.filter((_, i) => i !== idx),
    });

  const validateStep = () => {
    let newErrors = {};

    switch (activeStep) {
      case 0: // Personal Details
        if (!formData.name.trim()) newErrors.name = "Full name required";
        if (!formData.email.trim()) newErrors.email = "Email required";
        else if (!/\S+@\S+\.\S+/.test(formData.email))
          newErrors.email = "Invalid email";
        if (!formData.phone.trim()) newErrors.phone = "Phone required";
        else if (!/^\d{10}$/.test(formData.phone))
          newErrors.phone = "Phone must be exactly 10 digits";
        break;

      case 1: // Education
        formData.education.forEach((edu, idx) => {
          if (!edu.degree.trim())
            newErrors[`education_degree_${idx}`] = "Degree required";

          if (!edu.university.trim())
            newErrors[`education_university_${idx}`] = "University required";

          if (!edu.year.trim()) {
            newErrors[`education_year_${idx}`] = "Year required";
          } else if (!/^\d{4}$/.test(edu.year)) {
            newErrors[`education_year_${idx}`] = "Year must be 4 digits";
          } else {
            const yearNum = parseInt(edu.year, 10);
            const currentYear = new Date().getFullYear();
            if (yearNum < 1900 || yearNum > currentYear + 1) {
              newErrors[
                `education_year_${idx}`
              ] = `Year must be between 1900 and ${currentYear + 1}`;
            }
          }

          if (!edu.grade.trim()) {
            newErrors[`education_grade_${idx}`] = "Grade required";
          } else if (!/^\d+(\.\d+)?%?$/.test(edu.grade)) {
            newErrors[`education_grade_${idx}`] =
              "Grade must be a number or percentage";
          }
        });
        break;

      case 2: // Experience
        // formData.experience.forEach((exp, idx) => {
        //   if (!exp.company.trim())
        //     newErrors[`experience_company_${idx}`] = "Company required";
        //   if (!exp.jobTitle.trim())
        //     newErrors[`experience_jobTitle_${idx}`] = "Job title required";
        //   if (!exp.startDate.trim())
        //     newErrors[`experience_startDate_${idx}`] = "Start date required";
        //   if (!exp.endDate.trim())
        //     newErrors[`experience_endDate_${idx}`] = "End date required";
        // });
        if (!formData.resume) newErrors.resume = "Resume required";
        break;

      case 3: // Skills (optional)
        formData.skills.forEach((skill, idx) => {
          if (!skill.trim()) newErrors[`skill_${idx}`] = "Skill recommended";
        });
        break;

      case 4: // Review
        if (!formData.agree)
          newErrors.agree = "You must confirm before submitting";
        break;

      default:
        break;
    }

    setErrors(newErrors);

    // Skills step: allow navigation even if empty
    return activeStep === 3 || Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (!validateStep()) return;

    if (activeStep === steps.length - 1) {
      // Final step: submit the data
      handleSubmit();
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => setActiveStep(activeStep - 1);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      address: "",
      linkedin: "",
      skills: [""],
      education: [
        {
          degree: "",
          university: "",
          year: "",
          grade: "",
          certifications: [""],
        },
      ],
      experience: [
        {
          company: "",
          jobTitle: "",
          startDate: "",
          endDate: "",
          responsibilities: "",
        },
      ],
      resume: null,
      agree: false,
    });
  };

  const handleSubmit = async () => {
    try {
      // Map formData → API structure
      const payload = {
        fullName: formData.name,
        email: formData.email,
        phoneNumber: formData.phone,
        address: formData.address,
        profileUrl: formData.linkedin,
        skills: formData.skills.filter((s) => s.trim() !== ""),
        educationDTO: formData.education.map((edu) => ({
          degree: edu.degree,
          university: edu.university,
          yearOfPassing: edu.year ? parseInt(edu.year, 10) : null,
          grade: edu.grade,
          courses: edu.certifications.filter((c) => c.trim() !== ""),
        })),
        experienceDTO: formData.experience.map((exp) => ({
          companyName: exp.company,
          jobTitle: exp.jobTitle,
          startDate: exp.startDate || null,
          endDate: exp.endDate || null,
          responsibilities: exp.responsibilities,
        })),
        appliedFor: job?.title,
      };

      const form = new FormData();
      if (formData.resume) {
        form.append("file", formData.resume);
      }
      form.append("data", JSON.stringify(payload));

      await axios.post(
        "https://vikrantpatiloffice.in:8443/websitecontactus/jobs/apply",
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      onClose();
      resetForm();

      // Show success snackbar after 500ms delay to ensure modal is closed
      setTimeout(() => {
        showSnackbar("Application submitted successfully!", "success");
      }, 500);
    } catch (err) {
      console.error("API Error:", err.response?.data || err.message);
      showSnackbar("Failed to submit application. Please try again.", "error");
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        className="mt-20"
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Apply for {job?.title}
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stepper
            activeStep={activeStep}
            orientation={isSmallScreen ? "vertical" : "horizontal"} // vertical on small screens
            sx={{ mb: 4 }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step Content */}
          {activeStep === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "#0D3559", // focused border
                      },
                    },

                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#0D3559", // focused label color
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "#0D3559", // focused border
                      },
                    },

                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#0D3559", // focused label color
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => {
                    const onlyNums = e.target.value.replace(/\D/g, ""); // remove non-digits
                    if (onlyNums.length <= 10) {
                      handleChange({
                        target: {
                          name: "phone",
                          value: onlyNums,
                          type: "text",
                        },
                      });
                    }
                  }}
                  inputProps={{ maxLength: 10, inputMode: "numeric" }}
                  error={!!errors.phone}
                  helperText={errors.phone}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "#0D3559", // focused border
                      },
                    },

                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#0D3559", // focused label color
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "#0D3559", // focused border
                      },
                    },

                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#0D3559", // focused label color
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="LinkedIn / Portfolio URL"
                  name="linkedin"
                  value={formData.linkedin}
                  onChange={handleChange}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "#0D3559", // focused border
                      },
                    },

                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#0D3559", // focused label color
                    },
                  }}
                />
              </Grid>
            </Grid>
          )}

          {activeStep === 1 &&
            formData.education.map((edu, idx) => (
              <Box
                key={idx}
                sx={{
                  border: "1px solid #ccc",
                  borderRadius: 2,
                  p: 3,
                  mb: 3,
                  position: "relative",
                }}
              >
                {formData.education.length > 1 && (
                  <Button
                    size="small"
                    color="error"
                    sx={{ position: "absolute", top: 0, right: 8 }}
                    onClick={() => removeEducation(idx)}
                  >
                    Remove
                  </Button>
                )}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Degree (Specialization)"
                      name="degree"
                      value={edu.degree}
                      onChange={(e) => handleChange(e, "education", idx)}
                      error={!!errors[`education_degree_${idx}`]}
                      helperText={errors[`education_degree_${idx}`]}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&.Mui-focused fieldset": {
                            borderColor: "#0D3559", // focused border
                          },
                        },

                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#0D3559", // focused label color
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="University / Institution"
                      name="university"
                      value={edu.university}
                      onChange={(e) => handleChange(e, "education", idx)}
                      error={!!errors[`education_university_${idx}`]}
                      helperText={errors[`education_university_${idx}`]}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&.Mui-focused fieldset": {
                            borderColor: "#0D3559", // focused border
                          },
                        },

                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#0D3559", // focused label color
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Year of Passing"
                      name="year"
                      value={edu.year}
                      onChange={(e) => {
                        const onlyNums = e.target.value.replace(/\D/g, ""); // digits only
                        if (onlyNums.length <= 4) {
                          handleChange(
                            {
                              target: {
                                name: "year",
                                value: onlyNums,
                                type: "text",
                              },
                            },
                            "education",
                            idx
                          );
                        }
                      }}
                      inputProps={{ maxLength: 4, inputMode: "numeric" }}
                      error={!!errors[`education_year_${idx}`]}
                      helperText={errors[`education_year_${idx}`]}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&.Mui-focused fieldset": {
                            borderColor: "#0D3559", // focused border
                          },
                        },

                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#0D3559", // focused label color
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Grade / Percentage"
                      name="grade"
                      value={edu.grade}
                      onChange={(e) => {
                        let val = e.target.value.replace(/[^0-9.%]/g, ""); // allow only numbers, dot, %

                        // Auto-format logic
                        if (val && !val.includes("%")) {
                          const num = parseFloat(val);
                          if (!isNaN(num)) {
                            if (num > 10) {
                              // assume it's percentage
                              val = val + "%";
                            }
                          }
                        }

                        handleChange(
                          {
                            target: { name: "grade", value: val, type: "text" },
                          },
                          "education",
                          idx
                        );
                      }}
                      error={!!errors[`education_grade_${idx}`]}
                      helperText={errors[`education_grade_${idx}`]}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&.Mui-focused fieldset": {
                            borderColor: "#0D3559", // focused border
                          },
                        },

                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#0D3559", // focused label color
                        },
                      }}
                    />
                  </Grid>
                </Grid>

                {edu.certifications.map((cert, cIdx) => (
                  <Grid
                    container
                    spacing={2}
                    key={cIdx}
                    sx={{ mt: 2, alignItems: "center" }}
                  >
                    <Grid item xs={10}>
                      <TextField
                        fullWidth
                        label={`Certification ${cIdx + 1}`}
                        name="certifications"
                        value={cert}
                        onChange={(e) =>
                          handleChange(e, "education", idx, cIdx)
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "&.Mui-focused fieldset": {
                              borderColor: "#0D3559", // focused border
                            },
                          },

                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "#0D3559", // focused label color
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      {edu.certifications.length > 1 && (
                        <Button
                          color="error"
                          onClick={() => removeCertification(idx, cIdx)}
                        >
                          Remove
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                ))}
                <Button
                  variant="outlined"
                  sx={{ mt: 2 }}
                  onClick={() => addCertification(idx)}
                >
                  + Add Certification
                </Button>
              </Box>
            ))}
          {activeStep === 1 && (
            <Button variant="outlined" onClick={addEducation}>
              + Add Education
            </Button>
          )}

          {activeStep === 2 &&
            formData.experience.map((exp, idx) => (
              <Box
                key={idx}
                sx={{
                  border: "1px solid #ccc",
                  borderRadius: 2,
                  p: 3,
                  mb: 3,
                  position: "relative",
                }}
              >
                {formData.experience.length > 1 && (
                  <Button
                    size="small"
                    color="error"
                    sx={{ position: "absolute", top: 0, right: 8 }}
                    onClick={() => removeExperience(idx)}
                  >
                    Remove
                  </Button>
                )}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Company Name"
                      name="company"
                      value={exp.company}
                      onChange={(e) => handleChange(e, "experience", idx)}
                      // error={!!errors[`experience_company_${idx}`]}
                      // helperText={errors[`experience_company_${idx}`]}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&.Mui-focused fieldset": {
                            borderColor: "#0D3559", // focused border
                          },
                        },

                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#0D3559", // focused label color
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Job Title"
                      name="jobTitle"
                      value={exp.jobTitle}
                      onChange={(e) => handleChange(e, "experience", idx)}
                      // error={!!errors[`experience_jobTitle_${idx}`]}
                      // helperText={errors[`experience_jobTitle_${idx}`]}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&.Mui-focused fieldset": {
                            borderColor: "#0D3559", // focused border
                          },
                        },

                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#0D3559", // focused label color
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Start Date"
                      name="startDate"
                      InputLabelProps={{ shrink: true }}
                      value={exp.startDate}
                      onChange={(e) => handleChange(e, "experience", idx)}
                      // error={!!errors[`experience_startDate_${idx}`]}
                      // helperText={errors[`experience_startDate_${idx}`]}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&.Mui-focused fieldset": {
                            borderColor: "#0D3559", // focused border
                          },
                        },

                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#0D3559", // focused label color
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="End Date"
                      name="endDate"
                      InputLabelProps={{ shrink: true }}
                      value={exp.endDate}
                      onChange={(e) => handleChange(e, "experience", idx)}
                      // error={!!errors[`experience_endDate_${idx}`]}
                      // helperText={errors[`experience_endDate_${idx}`]}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&.Mui-focused fieldset": {
                            borderColor: "#0D3559", // focused border
                          },
                        },

                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#0D3559", // focused label color
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Responsibilities / Achievements"
                      name="responsibilities"
                      value={exp.responsibilities}
                      onChange={(e) => handleChange(e, "experience", idx)}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&.Mui-focused fieldset": {
                            borderColor: "#0D3559", // focused border
                          },
                        },

                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#0D3559", // focused label color
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}
          {activeStep === 2 && (
            <Button variant="outlined" onClick={addExperience}>
              + Add Experience
            </Button>
          )}
          {activeStep === 2 && (
            <Box sx={{ mt: 2 }}>
              <input
                type="file"
                name="resume"
                accept=".pdf,.doc,.docx"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (!file) return;

                  const allowedTypes = [
                    "application/pdf",
                    "application/msword",
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                  ];
                  const maxSize = 5 * 1024 * 1024; // 5MB

                  if (!allowedTypes.includes(file.type)) {
                    setErrors({
                      ...errors,
                      resume: "Only PDF or DOC/DOCX files allowed",
                    });
                    setFormData({ ...formData, resume: null });
                    return;
                  }

                  if (file.size > maxSize) {
                    setErrors({ ...errors, resume: "File size must be ≤ 5MB" });
                    setFormData({ ...formData, resume: null });
                    return;
                  }

                  setErrors({ ...errors, resume: null });
                  setFormData({ ...formData, resume: file });
                }}
              />

              {formData.resume && (
                <Typography sx={{ mt: 1 }}>
                  Uploaded: {formData.resume.name}
                </Typography>
              )}
              {errors.resume && (
                <Typography color="error">{errors.resume}</Typography>
              )}
            </Box>
          )}

          {activeStep === 3 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Skills
              </Typography>
              {formData.skills.map((skill, idx) => (
                <Grid
                  container
                  spacing={2}
                  key={idx}
                  sx={{ mb: 1, alignItems: "center" }}
                >
                  <Grid item xs={10}>
                    <TextField
                      fullWidth
                      label={`Skill ${idx + 1}`}
                      name="skills"
                      value={skill}
                      onChange={(e) => handleChange(e, "skills", idx)}
                      error={!!errors[`skill_${idx}`]}
                      helperText={errors[`skill_${idx}`]}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&.Mui-focused fieldset": {
                            borderColor: "#0D3559", // focused border
                          },
                        },

                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#0D3559", // focused label color
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    {formData.skills.length > 1 && (
                      <Button color="error" onClick={() => removeSkill(idx)}>
                        Remove
                      </Button>
                    )}
                  </Grid>
                </Grid>
              ))}
              <Button variant="outlined" onClick={addSkill}>
                + Add Skill
              </Button>
            </Box>
          )}

          {activeStep === 4 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Review Your Application
              </Typography>
              <Typography>
                Name:{" "}
                {formData.name || (
                  <span style={{ color: "red" }}>Not provided</span>
                )}
              </Typography>
              <Typography>
                Email:{" "}
                {formData.email || (
                  <span style={{ color: "red" }}>Not provided</span>
                )}
              </Typography>
              <Typography>
                Phone:{" "}
                {formData.phone || (
                  <span style={{ color: "red" }}>Not provided</span>
                )}
              </Typography>
              <Typography>
                Address:{" "}
                {formData.address || (
                  <span style={{ color: "red" }}>Not provided</span>
                )}
              </Typography>
              <Typography>
                LinkedIn:{" "}
                {formData.linkedin || (
                  <span style={{ color: "red" }}>Not provided</span>
                )}
              </Typography>

              <Typography variant="h6" sx={{ mt: 3 }}>
                Skills
              </Typography>
              {formData.skills.map((s, idx) => (
                <Typography key={idx}>
                  {idx + 1}.{" "}
                  {s || <span style={{ color: "red" }}>Not provided</span>}
                </Typography>
              ))}

              <Typography variant="h6" sx={{ mt: 3 }}>
                Education
              </Typography>
              {formData.education.map((edu, idx) => (
                <Box key={idx} sx={{ mb: 1 }}>
                  <Typography>
                    Degree:{" "}
                    {edu.degree || (
                      <span style={{ color: "red" }}>Not provided</span>
                    )}
                  </Typography>
                  <Typography>
                    University:{" "}
                    {edu.university || (
                      <span style={{ color: "red" }}>Not provided</span>
                    )}
                  </Typography>
                  <Typography>
                    Year:{" "}
                    {edu.year || (
                      <span style={{ color: "red" }}>Not provided</span>
                    )}
                  </Typography>
                  <Typography>
                    Grade:{" "}
                    {edu.grade || (
                      <span style={{ color: "red" }}>Not provided</span>
                    )}
                  </Typography>
                  <Typography>
                    Certifications:{" "}
                    {edu.certifications.length ? (
                      edu.certifications.join(", ")
                    ) : (
                      <span style={{ color: "red" }}>None</span>
                    )}
                  </Typography>
                </Box>
              ))}

              <Typography variant="h6" sx={{ mt: 3 }}>
                Experience
              </Typography>
              {formData.experience.map((exp, idx) => (
                <Box key={idx} sx={{ mb: 1 }}>
                  <Typography>
                    Company:{" "}
                    {exp.company || (
                      <span style={{ color: "red" }}>Not provided</span>
                    )}
                  </Typography>
                  <Typography>
                    Job Title:{" "}
                    {exp.jobTitle || (
                      <span style={{ color: "red" }}>Not provided</span>
                    )}
                  </Typography>
                  <Typography>
                    Start Date:{" "}
                    {exp.startDate || (
                      <span style={{ color: "red" }}>Not provided</span>
                    )}
                  </Typography>
                  <Typography>
                    End Date:{" "}
                    {exp.endDate || (
                      <span style={{ color: "red" }}>Not provided</span>
                    )}
                  </Typography>
                  <Typography>
                    Responsibilities:{" "}
                    {exp.responsibilities || (
                      <span style={{ color: "red" }}>Not provided</span>
                    )}
                  </Typography>
                </Box>
              ))}

              <Typography variant="h6" sx={{ mt: 3 }}>
                Role
              </Typography>
              <Typography>{job?.title}</Typography>

              <Typography variant="h6" sx={{ mt: 3 }}>
                Resume
              </Typography>
              <Typography>
                {formData.resume ? (
                  formData.resume.name
                ) : (
                  <span style={{ color: "red" }}>Resume not uploaded</span>
                )}
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    name="agree"
                    checked={formData.agree}
                    onChange={handleChange}
                  />
                }
                label="I confirm that the above information is correct"
              />
              {errors.agree && (
                <Typography color="error">{errors.agree}</Typography>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          {activeStep > 0 && (
            <Button variant="outlined" onClick={handleBack}>
              Back
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={activeStep === 4 && !formData.agree} // Only enable submit if agreed
          >
            {activeStep === steps.length - 1 ? "Submit Application" : "Next"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Centered Snackbar */}
      {/* <Snackbar
        open={snackbarOpen}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ zIndex: 1500 }} // above the modal
      >
        <Alert
          onClose={handleCloseSnackbar} // shows the close icon
          severity={snackbarType}
          sx={{ width: "100%", textAlign: "center" }}
          variant="filled"
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleCloseSnackbar}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          {snackbarType === "success" ? successMsg : errorMsg}
        </Alert>
      </Snackbar> */}
    </>
  );
};

export default ApplyModal;
